import React from "react";
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  Cell,
} from "@table-library/react-table-library/table";
import { Icon } from "@iconify/react";
import {
  createStyles,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import { CustomTheme } from "../../../../types/customTheme";
import {
  HeaderCellSort,
  useSort,
} from "@table-library/react-table-library/sort";
import CardCommon from "../../../../components/card/CardCommon";

const useStyles = (bodyLength: number) =>
  makeStyles((theme: CustomTheme) =>
    createStyles({
      sortIcon: {
        "& svg": {
          fill: theme.palette.custom.orange.contrastText,
          width: "10px", // Set a specific width for the icon
          height: "10px",
        },
      },
      headerStyle: {
        "&:last-of-type": {
          backgroundColor: theme.palette.background.table_header_background,
          color: theme.palette.custom.orange.contrastText,
          height: "40px",
          fontWeight: "bold",
        },
      },
      headerRow: {
        fontSize: "13px",
        cursor: "pointer",
      },
      baseCellRow: {
        placeItems: "start",
        "& .css-16gtl2w": {
          fontWeight: "bold",
        },
        height: "40px",
        "&:nth-of-type(1)": {
          left: "0px",
          minWidth: `${20}%`,
          width: `${20}%`,
        },
        [`&:nth-of-type(n+2):nth-of-type(-n+7)`]: {
          minWidth: `${80 / 7}%`,
          width: `${80 / 7}%`,
          "& .css-16gtl2w": {
            display: "flex",
            justifyContent: "start",
            fontWeight: "bold",
          },
        },
        [`&:nth-of-type(n+8)`]: {
          minWidth: `${80 / 7}%`,
          width: `${80 / 7}%`,
          "& .css-16gtl2w": {
            display: "flex",
            justifyContent: "end",
            fontWeight: "bold",
          },
        },
      },
      oddRow: {
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.background.paper,
        },
        "&:nth-of-type(even)": {
          backgroundColor: theme.palette.background.entity_background,
        },
      },
      evenRow: {
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.background.paper,
        },
        "&:nth-of-type(even)": {
          backgroundColor: theme.palette.background.entity_background,
        },
      },
      totalRow: {
        color: theme.palette.custom["green"].contrastText,
        backgroundColor: theme.palette.custom.green.main,
        fontWeight: "bold",
        "&:hover": {
          backgroundColor: theme.palette.custom.green.light,
        },
      },
    }),
  );

export interface ShiftHeaderProps {
  dummyData: any;
}

const NormalSalesTable: React.FunctionComponent<ShiftHeaderProps> = ({
  dummyData,
}) => {
  const theme: CustomTheme = useTheme();
  const maxWidth = useMediaQuery("(max-width: 1300px)");

  // Separate total row from other rows
  const totalRow = dummyData.find((row: any) => row.month === "Total");
  const sortableRows = dummyData.filter((row: any) => row.month !== "Total");

  // Map sortable rows to nodes
  const nodes = sortableRows.map((row: any, i: number) => ({
    id: `${i}`,
    month: row.month ?? "",
    data: {
      card: row.card ?? 0,
      cash: row.cash ?? 0,
      discount: row.discount ?? 0,
      otherPayments: row.otherPayments ?? 0,
      saleCount: row.saleCount ?? 0,
      saleTotal: row.saleTotal ?? 0,
      total: row.total ?? 0,
    },
  }));

  // Total node
  const totalNode = totalRow
    ? {
        id: "total",
        month: totalRow.month,
        data: {
          card: totalRow.card ?? 0,
          cash: totalRow.cash ?? 0,
          discount: totalRow.discount ?? 0,
          otherPayments: totalRow.otherPayments ?? 0,
          saleCount: totalRow.saleCount ?? 0,
          saleTotal: totalRow.saleTotal ?? 0,
          total: totalRow.total ?? 0,
        },
        isTotal: true,
      }
    : null;

  const monthOrder: { [key: string]: number } = {
    January: 0,
    February: 1,
    March: 2,
    April: 3,
    May: 4,
    June: 5,
    July: 6,
    August: 7,
    September: 8,
    October: 9,
    November: 10,
    December: 11,
  };

  function parseMonthYear(monthYear: string) {
    if (!monthYear) return new Date(0); // fallback to earliest
    const [yearStr, monthStr] = monthYear.split(" ");
    const year = parseInt(yearStr, 10);
    const month = monthOrder[monthStr] ?? 0;
    return new Date(year, month, 1);
  }

  // Sort setup
  const sort = useSort(
    { nodes },
    {},
    {
      sortFns: {
        MONTH: (array) =>
          array.sort((a, b) => {
            const dateA = parseMonthYear(a.month);
            const dateB = parseMonthYear(b.month);
            return dateA.getTime() - dateB.getTime();
          }),
        CARD: (array) => array.sort((a, b) => b.data.card - a.data.card),
        CASH: (array) => array.sort((a, b) => b.data.cash - a.data.cash),
        DISCOUNT: (array) =>
          array.sort((a, b) => b.data.discount - a.data.discount),
        OTHER: (array) =>
          array.sort((a, b) => b.data.otherPayments - a.data.otherPayments),
        COUNT: (array) =>
          array.sort((a, b) => b.data.saleCount - a.data.saleCount),
        SALE_TOTAL: (array) =>
          array.sort((a, b) => b.data.saleTotal - a.data.saleTotal),
        TOTAL: (array) => array.sort((a, b) => b.data.total - a.data.total),
      },
      sortIcon: {
        margin: "0px",
        iconDefault: (
          <Icon
            icon="fluent:chevron-up-down-16-filled"
            width="44"
            height="44"
          />
        ),
        iconUp: <ArrowDropDownIcon />,
        iconDown: <ArrowDropUpIcon />,
      },
    },
  );

  const COLUMNS = [
    { label: "Month", sortKey: "MONTH", renderCell: (item: any) => item.month },

    {
      label: "Orders",
      sortKey: "COUNT",
      renderCell: (item: any) => item.data.saleCount,
    },
    {
      label: "Sub Total",
      sortKey: "SALE_TOTAL",
      renderCell: (item: any) => item.data.saleTotal,
    },
    {
      label: "Discount",
      sortKey: "DISCOUNT",
      renderCell: (item: any) => item.data.discount,
    },
    {
      label: "Total",
      sortKey: "TOTAL",
      renderCell: (item: any) => item.data.total,
    },
    {
      label: "Cash Pay",
      sortKey: "CASH",
      renderCell: (item: any) => item.data.cash,
    },
    {
      label: "Card Pay",
      sortKey: "CARD",
      renderCell: (item: any) => item.data.card,
    },
    {
      label: "Other Pay",
      sortKey: "OTHER",
      renderCell: (item: any) => item.data.otherPayments,
    },
  ];

  const classes: any = useStyles(nodes.length)();

  return (
    <CardCommon>
      <Table
        data={{ nodes }}
        sort={sort}
        layout={{
          custom: true,
          horizontalScroll: false, // 3 columns, no scroll needed
        }}
      >
        {(tableList: any) => (
          <>
            {/* Header */}
            <Header style={{ zIndex: 0 }}>
              <HeaderRow className={classes.headerStyle}>
                {COLUMNS.map((column, idx) => (
                  <HeaderCellSort
                    key={idx}
                    sortKey={column.sortKey}
                    pinLeft={idx === 0}
                    className={classes.baseCellRow}
                    style={{
                      paddingLeft: idx === 0 ? "12px" : "0px",
                      color: theme.palette.custom.orange.contrastText,
                    }}
                  >
                    <Typography
                      variant="caption"
                      style={{
                        color: theme.palette.custom.orange.contrastText,
                        fontWeight: "bold",
                      }}
                    >
                      {column.label}
                    </Typography>
                  </HeaderCellSort>
                ))}
              </HeaderRow>
            </Header>

            {/* Body */}
            <Body>
              {/* Data rows */}
              {tableList.map((item: any, rowIndex: number) => (
                <Row
                  key={rowIndex}
                  item={item}
                  className={
                    rowIndex % 2 === 0 ? classes.evenRow : classes.oddRow
                  }
                >
                  {COLUMNS.map((column, idx) => {
                    const value = column.renderCell(item);
                    const displayValue =
                      idx !== 1 && idx !== 0
                        ? parseFloat(value || 0).toFixed(2) // Sale Total
                        : value || "0"; // Month / Sale Count
                    return (
                      <Cell
                        key={idx}
                        className={classes.baseCellRow}
                        style={{
                          display: "flex",
                          justifyContent: idx === 7 ? "end" : "start",
                          paddingLeft: idx === 0 ? "12px" : "0px",
                          paddingRight: idx === 7 ? "12px" : "0px",
                        }}
                      >
                        <Typography
                          variant="body2"
                          align={idx === 7 ? "right" : "left"}
                        >
                          {displayValue}
                        </Typography>
                      </Cell>
                    );
                  })}
                </Row>
              ))}

              {/* Total row */}
              {totalNode && (
                <Row key="total" item={totalNode} className={classes.totalRow}>
                  {COLUMNS.map((column, idx) => {
                    const value = column.renderCell(totalNode);
                    const displayValue =
                      idx !== 1 && idx !== 0
                        ? parseFloat(value || 0).toFixed(2)
                        : value || "0";

                    return (
                      <Cell
                        key={idx}
                        className={classes.baseCellRow}
                        style={{
                          backgroundColor: theme.palette.custom.green.main,
                          display: "flex",
                          justifyContent: idx === 7 ? "end" : "start",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: "bold",
                            paddingLeft: idx === 0 ? "12px" : "0px",
                            paddingRight: idx === 7 ? "12px" : "0px",
                          }}
                          align={idx === 7 ? "right" : "left"}
                        >
                          {displayValue}
                        </Typography>
                      </Cell>
                    );
                  })}
                </Row>
              )}
            </Body>
          </>
        )}
      </Table>
    </CardCommon>
  );
};

export default NormalSalesTable;
